import React, { useState, useEffect } from "react";
import { View, FlatList, StyleSheet, Dimensions, Platform } from "react-native";
import Placeholder from "../general/Placeholder/Placeholder";
import { useGenerateKeywordsMutation, useSearchYoutubeVideosLazyQuery } from "../../graphql/generated/graphql";
import Loader from "../common/status/Loader/Loader";
import { isLoading } from "expo-font";
import { announceForAccessibility } from "../../utils/accessibility";
import Sentry from "../../utils/sentry";
import YoutubeVideoPlayer from "./YoutubeVideoPlayer";

const extractTitleAndDescription = (keyword) => {
  const match = /Title: (.+?) \| Description: (.*)/.exec(keyword);
  return {
    title: match ? match[1] : "",
    description: match ? match[2] : "",
  };
};

const YoutubeSearch = ({ isMobile, keyword }) => {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialFetchCompleted, setInitialFetchCompleted] = useState(false);
  const [generateKeywordsMutation] = useGenerateKeywordsMutation();
  const [searchYoutubeVideos] = useSearchYoutubeVideosLazyQuery();

  useEffect(() => {
    if (Platform.OS === "ios" && !isLoading) {
      announceForAccessibility({ message: "Page is loaded", queue: false });
    }
  }, [isLoading]);

  const cleanKeyword = keyword
    .replace(/<[^>]+>/g, "")
    .split(".")
    .map((str) => str.trim())
    .join(" ");

  const { title, description } = extractTitleAndDescription(cleanKeyword);

  useEffect(() => {
    const fetchVideos = async (title, description) => {
      setLoading(true);
      try {
        const { data } = await generateKeywordsMutation({
          variables: {
            title: title,
            description: description,
          },
        });

        const generatedKeywords = data?.generateYoutubeKeywords;
        if (!generatedKeywords) {
          setVideos([]);
          setLoading(false);
          setInitialFetchCompleted(true);
          return;
        }

        const { data: searchData } = await searchYoutubeVideos({
          variables: { query: generatedKeywords },
        });

        const videoResults = (searchData?.searchYoutubeVideos ?? []).map((item) => ({
          id: item.videoId,
          title: item.title,
          description: item.description,
        }));

        setVideos(videoResults);
        setLoading(false);
        setInitialFetchCompleted(true);
      } catch (error) {
        Sentry.captureException(error);
        setLoading(false);
        setInitialFetchCompleted(true);
      }
    };

    if (cleanKeyword) {
      fetchVideos(title, description);
    }
  }, [title, description]);

  return (
    <View style={{ flex: 1 }}>
      {loading ? (
        <View accessible={true} role="alert" aria-live="assertive" style={styles.loaderContainer}>
          <Loader />
        </View>
      ) : !loading && initialFetchCompleted && videos.length === 0 ? (
        <Placeholder
          accessible={!loading}
          title="No Videos to See Here"
          subtitle="We are past our current capacity for videos, please check back here soon"
        />
      ) : (
        <FlatList
          contentContainerStyle={styles.list}
          data={videos}
          role="list"
          accessibilityRole="list"
          numColumns={isMobile ? 1 : 2}
          keyExtractor={(item) => item.id}
          renderItem={({ item }) => <YoutubeVideoPlayer id={item.id} />}
        />
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  list: {
    gap: 20,
  },
  loaderContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
  },
});

export default YoutubeSearch;
